/*
 * Zoey & Lex home page behavior. Loaded with defer by the home sections that need it
 * (zl-gift-boxes, zl-gift-note, zl-custom-gifting); runs once however many load it.
 *
 *  - Add to Bag (form[data-zl-add]): adds without leaving the page, updates the
 *    bag count through the theme's `cart:updated` event, fires `cart:open` so a
 *    cart drawer can open, and shows "Added to Bag" for a moment. Without JS (or
 *    if the request fails) the form posts normally and Shopify opens the cart page.
 *  - Gift note (sections/zl-gift-note): writes each example note out letter by
 *    letter, like a pen on a tag, once the tag is on screen.
 *  - Custom gifting form (form[data-zl-gift-form]): checks the fields before it
 *    sends, names the first one that needs fixing, and moves focus to the
 *    thank-you message after a successful send.
 *
 * Re-runs for a section the theme editor reloads (shopify:section:load).
 */
(() => {
  if (window.zlHome) return;
  window.zlHome = true;

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  // Add to Bag ---------------------------------------------------------------
  function bindAdd(root) {
    root.querySelectorAll('form[data-zl-add]').forEach((form) => {
      if (form.dataset.zlBound) return;
      form.dataset.zlBound = '1';
      form.addEventListener('submit', async (e) => {
        const btn = form.querySelector('button[type=submit]');
        if (!window.fetch || !btn) return;
        e.preventDefault();
        const label = btn.textContent;
        btn.disabled = true;
        try {
          const add = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          if (!add.ok) throw new Error('add failed');
          const cart = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js').then((r) => r.json());
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cart.item_count, cart } }));
          document.dispatchEvent(new CustomEvent('cart:open'));
          btn.textContent = btn.dataset.addedLabel || label;
          await wait(2200);
          btn.textContent = label;
          btn.disabled = false;
        } catch {
          btn.disabled = false;
          form.submit();
        }
      });
    });
  }

  // Gift note ----------------------------------------------------------------
  function bindNotes(root) {
    root.querySelectorAll('[data-zl-note-cycle]').forEach((box) => {
      if (box.dataset.zlBound || reduceMotion) return;
      box.dataset.zlBound = '1';
      const lines = [...box.querySelectorAll('[data-zl-note]')];
      const notes = lines.map((n) => n.textContent.trim()).filter(Boolean);
      // The first line is the pen: every note is written into it in turn.
      const el = lines[0];
      if (!el || notes.length < 2) return;
      let i = 0;
      const write = async (text) => {
        for (let n = 1; n <= text.length; n++) {
          if (!el.isConnected) return;
          el.textContent = text.slice(0, n);
          await wait(55);
        }
      };
      const loop = async () => {
        while (el.isConnected) {
          await wait(3200);
          el.style.transition = 'opacity .5s';
          el.style.opacity = '0';
          await wait(500);
          i = (i + 1) % notes.length;
          el.textContent = '';
          el.style.opacity = '1';
          await write(notes[i]);
        }
      };
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            io.disconnect();
            loop();
          }
        },
        { threshold: 0.4 },
      );
      io.observe(box);
    });
  }

  // Custom gifting form ------------------------------------------------------
  function bindGiftForm(root) {
    root.querySelectorAll('form[data-zl-gift-form]').forEach((form) => {
      const done = form.querySelector('[data-zl-done]');
      if (done && !form.dataset.zlBound) {
        form.dataset.zlBound = '1';
        done.focus();
        return;
      }
      if (form.dataset.zlBound) return;
      form.dataset.zlBound = '1';
      const err = form.querySelector('[data-zl-error]');
      const btn = form.querySelector('button[type=submit]');
      form.addEventListener('submit', (e) => {
        const bad = [...form.querySelectorAll('[required]')].find((f) => !f.checkValidity());
        if (bad) {
          e.preventDefault();
          const label = form.querySelector(`label[for="${bad.id}"]`)?.childNodes[0]?.textContent?.trim() || '';
          if (err) {
            err.textContent = (err.dataset.checkTemplate || '[field]').replace('[field]', label);
            err.hidden = false;
          }
          bad.focus();
          return;
        }
        if (err) err.hidden = true;
        if (btn) {
          btn.disabled = true;
          btn.textContent = btn.dataset.sendingLabel || btn.textContent;
        }
      });
    });
  }

  const init = (root = document) => {
    bindAdd(root);
    bindNotes(root);
    bindGiftForm(root);
  };
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
